import {
  resolveCallbackFunction,
  tryCatchAwaitable,
} from '@shared/helpers/utils.helpers';
import { OptionsObject, useSnackbar as notistackUseSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useErrorHandler } from './use-error-handler.hooks';

export const useSnackbar = () => {
  const { enqueueSnackbar: rawEnqueue, closeSnackbar } = notistackUseSnackbar();

  const enqueueSnackbar: typeof rawEnqueue = useCallback(
    (...args: Parameters<typeof rawEnqueue>) => {
      closeSnackbar();
      return rawEnqueue(...args);
    },
    [rawEnqueue, closeSnackbar],
  );

  return { enqueueSnackbar, closeSnackbar };
};

const ERROR_DEDUP_WINDOW_MS = 3000;
const recentErrorMessageTimestamps = new Map<string, number>();

/**
 * Checks if a message should be shown based on the last time it was shown
 * @param message - The message to check
 * @param timeWindow - The time window in milliseconds
 * @returns True if the message should be shown
 */
const shouldShowMessage = (
  message: string,
  timeWindow: number = ERROR_DEDUP_WINDOW_MS,
): boolean => {
  const now = Date.now();
  const last = recentErrorMessageTimestamps.get(message);

  if (last && now - last < timeWindow) return false;

  recentErrorMessageTimestamps.set(message, now);

  return true;
};

/**
 * Snackbar that is prevented from showing the same message within a short window of time
 * @param timeWindow - The time window in milliseconds (to override the default)
 * @returns A function that enqueues a snackbar message
 */
export const useDebouncedSnackbar = (timeWindow?: number) => {
  const { enqueueSnackbar } = useSnackbar();
  return useCallback(
    (message: string, options?: OptionsObject) => {
      if (shouldShowMessage(message, timeWindow)) {
        enqueueSnackbar(message, options);
      }
    },
    [enqueueSnackbar, timeWindow],
  );
};

export interface ISnackbarOptions extends OptionsObject {
  errorMessage?: string;
  logError?: boolean;
  successMessage?: string;
}

/**
 * A function that takes a promise or function returning a promise and show a message to the user when that promise is resolved
 * @param promiseOrCallback
 * @param options - The options for the snackbar
 * @returns The result of the promise or function returning a promise
 */

type PromiseSnackbarCallback = {
  <T>(
    factory: () => T | Promise<T | Error>,
    options?: ISnackbarOptions,
  ): Promise<Exclude<T, Error>>;
  <T>(
    valueOrPromise: T | Promise<T | Error>,
    options?: ISnackbarOptions,
  ): Promise<Exclude<T, Error>>;
};

/**
 * Returns a function that that takes a promise or function returning and show a message to the user when that promise is resolved
 * @returns A function that enqueues a snackbar message when the promise is resolved
 */
export function usePromiseSnackbar(): PromiseSnackbarCallback {
  const enqueueSnackbar = useDebouncedSnackbar();
  const errorHandler = useErrorHandler();

  return useCallback<PromiseSnackbarCallback>(
    async (promiseOrCallback, options = {}) => {
      const response = await tryCatchAwaitable(
        resolveCallbackFunction(promiseOrCallback),
      );

      if (!response.ok) {
        errorHandler(response.error);
        // TODO: Make all consumers handle errors themselves (not just reject the promise)
        return Promise.reject(response.error);
      }

      const {
        successMessage: message,
        variant = 'success',
        ...snackbarOptions
      } = options;

      if (message) {
        enqueueSnackbar(message, {
          ...snackbarOptions,
          variant,
        });
      }
      return response.value;
    },
    [enqueueSnackbar, errorHandler],
  );
}
